/**
 * Sharing API
 *
 * Mock sharing service for Pattern B batch sharing.
 * Based on Doc 5: Communities, Sharing & Social
 */

import type {
  SharedItem,
  BatchShareRequest,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, timestamp } from './mockDb'
import { safeExecute } from './client'

const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Sharing API
 */
export const sharingApi = {
  async batchShare(request: BatchShareRequest): Promise<ApiResponse<SharedItem[]>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      const shared: SharedItem[] = []
      const now = timestamp()

      for (const item of request.items) {
        for (const communityId of request.communityIds) {
          const record = mockDb.insert<SharedItem>('sharedItems', {
            userId,
            itemId: item.itemId,
            itemType: item.itemType,
            communityId,
            message: request.message,
            sharedAt: now,
          } as SharedItem)
          shared.push(record)
        }
      }

      return shared
    })
  },

  async getSharedItems(communityId: UUID): Promise<ApiResponse<SharedItem[]>> {
    return safeExecute(() => {
      return mockDb.findWhere<SharedItem>(
        'sharedItems',
        (s) => s.communityId === communityId
      )
    })
  },

  async getUserSharedItems(): Promise<ApiResponse<SharedItem[]>> {
    return safeExecute(() => {
      const userId = getCurrentUserId()
      if (!userId) throw new Error('Not authenticated')

      return mockDb.findWhere<SharedItem>(
        'sharedItems',
        (s) => s.userId === userId
      )
    })
  },

  async removeShare(shareId: UUID): Promise<ApiResponse<boolean>> {
    return safeExecute(() => {
      return mockDb.delete('sharedItems', shareId)
    })
  },
}
